import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { Creative } from '../types'
import { flushPersistNow, useCreativeStore } from './creative-store'
import { useIdeaStore } from './idea-store'
import { isUserMediaChange, saveUserMediaEdit } from './user-media-edit'

const now = () => new Date().toISOString()

interface DraftCreativeContextValue {
  /** The draft creative — what the editors should render. */
  draft: Creative
  /** The pre-edit snapshot, kept stable for the lifetime of the provider. */
  snapshot: Creative
  /** True when draft.content differs from snapshot.content. */
  isDirty: boolean
  /** Replace draft.content with a partial patch. Mirrors useCreativeStore.updateCreativeContent. */
  setDraftContent: (contentUpdates: Record<string, unknown>) => void
  /**
   * Push the draft into the real store, mirror to idea-store, fire a persist.
   * Rejects when an upload post's media swap is refused (published, mid-publish, …).
   */
  commit: () => Promise<void>
}

const DraftCreativeContext = createContext<DraftCreativeContextValue | null>(null)

interface DraftCreativeProviderProps {
  creativeId: string
  children: ReactNode
}

/**
 * Wraps an editor subtree so its writes go into a local draft instead of the
 * real creative store. The preview outside this subtree keeps reading the
 * real store and stays frozen at the pre-edit state. On commit the draft is
 * written back in one shot; on unmount-without-commit the draft is dropped.
 */
export function DraftCreativeProvider({ creativeId, children }: DraftCreativeProviderProps) {
  const liveCreative = useCreativeStore(s => s.creatives.find(c => c.id === creativeId))

  const snapshotRef = useRef<Creative | null>(null)
  if (snapshotRef.current === null && liveCreative) {
    snapshotRef.current = structuredClone(liveCreative)
  }
  const snapshot = snapshotRef.current!

  const [draft, setDraft] = useState<Creative>(() => structuredClone(snapshot))

  const setDraftContent = useCallback((contentUpdates: Record<string, unknown>) => {
    setDraft(prev => ({
      ...prev,
      content: { ...prev.content, ...contentUpdates },
      updatedAt: now(),
    }))
  }, [])

  const commit = useCallback(async () => {
    const before = snapshot.content as unknown as Record<string, unknown>
    const after = draft.content as unknown as Record<string, unknown>
    if (snapshot.projectId && isUserMediaChange(before, after)) {
      await saveUserMediaEdit({ creativeId, projectId: snapshot.projectId, before, after })
      return
    }
    useCreativeStore.getState().updateCreative(creativeId, { content: draft.content })
    useIdeaStore.getState().patchCreativeContent(creativeId, after)
    flushPersistNow(creativeId)
  }, [creativeId, draft.content, snapshot])

  const isDirty = useMemo(() => {
    return JSON.stringify(draft.content) !== JSON.stringify(snapshot.content)
  }, [draft.content, snapshot.content])

  const value = useMemo<DraftCreativeContextValue>(
    () => ({
      draft,
      snapshot,
      isDirty,
      setDraftContent,
      commit,
    }),
    [draft, snapshot, isDirty, setDraftContent, commit]
  )

  if (!liveCreative) return null

  return <DraftCreativeContext.Provider value={value}>{children}</DraftCreativeContext.Provider>
}

/**
 * Read the draft creative when inside a DraftCreativeProvider; returns null
 * outside one. The modal passes the draft into getEditor() as the `creative`
 * prop so the editor renders the user's pending edits.
 */
export function useDraftCreative(): Creative | null {
  return useContext(DraftCreativeContext)?.draft ?? null
}

/** Subscribe to dirty/commit/etc — for the modal footer. */
export function useDraftCreativeContext(): DraftCreativeContextValue | null {
  return useContext(DraftCreativeContext)
}

export function useUpdateCreativeContent() {
  const ctx = useContext(DraftCreativeContext)
  const realUpdate = useCreativeStore(s => s.updateCreativeContent)
  return useMemo(() => {
    if (!ctx) return realUpdate
    return (_creativeId: string, fields: Record<string, unknown>) => {
      ctx.setDraftContent(fields)
    }
  }, [ctx, realUpdate])
}

export function useFlushPersistNow() {
  const ctx = useContext(DraftCreativeContext)
  return useMemo(() => {
    if (!ctx) return flushPersistNow
    return (_id: string) => {}
  }, [ctx])
}
